/**
 * GA4 Provider Component
 *
 * Initializes Google Analytics 4 and sets user properties for segmentation.
 * This is a minimal integration that relies on GA4's enhanced measurement
 * for automatic page view tracking.
 *
 * User properties set:
 * - user_role: The user's primary role (admin, manager, technician, etc.)
 * - tenant_id: The current tenant ID for multi-tenant analysis
 * - tenant_name: The current tenant name for readability in reports
 */

import { useEffect, useRef, type FC, type ReactNode } from 'react';
import { useAuth } from '@/features/auth/contexts/EnhancedAuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { initGA4, setUserProperties, setUserId } from '@/lib/analytics/ga4';

interface GA4ProviderProps {
  children: ReactNode;
}

export const GA4Provider: FC<GA4ProviderProps> = ({ children }) => {
  const { user, userProfile } = useAuth();
  const { currentTenant } = useTenant();
  const prevUserIdRef = useRef<string | null>(null);

  // Initialize GA4 on mount
  useEffect(() => {
    initGA4();
  }, []);

  // Set user ID when auth state changes
  useEffect(() => {
    const currentUserId = user?.id ?? null;

    // Only update if user ID actually changed
    if (prevUserIdRef.current !== currentUserId) {
      setUserId(currentUserId);
      prevUserIdRef.current = currentUserId;
    }
  }, [user?.id]);

  // Set user properties when profile or tenant changes
  useEffect(() => {
    if (!user) {
      // Clear properties on logout
      setUserProperties({});
      return;
    }

    // Get primary role name
    const primaryRole = userProfile?.roles?.[0]?.name;

    // Build properties object, only including defined values
    const properties: { user_role?: string; tenant_id?: string; tenant_name?: string } = {};
    if (primaryRole) properties.user_role = primaryRole;
    if (currentTenant?.id) properties.tenant_id = currentTenant.id;
    if (currentTenant?.tenant_name) properties.tenant_name = currentTenant.tenant_name;

    setUserProperties(properties);
  }, [user, userProfile?.roles, currentTenant?.id, currentTenant?.tenant_name]);

  return children;
};

export default GA4Provider;
